import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, MapPin, Clock, DollarSign, Loader2, ShieldCheck } from "lucide-react";
import DashboardShell from "@/layouts/ProviderDashboardLayout";
import api from "@/lib/api";
import { AttachmentThumb } from "@/components/servicerequest/AttachmentsStep";
import { budgetLabel } from "@/components/servicerequest/ReviewStep";
import EstimateForm from "@/components/servicerequest/EstimateForm";
import { SR_STYLES } from "@/components/servicerequest/QuestionField";
import { useDocumentTitle } from "@/lib/useDocumentTitle";

const REASONS = [["too_far", "Too far away"], ["too_busy", "Fully booked"], ["budget_low", "Budget too low"], ["not_my_service", "Not a service I offer"], ["not_a_fit", "Other / not a fit"]];

function DeclineBox({ oppId, onDone }) {
  const [reason, setReason] = useState("not_a_fit");
  const submit = async () => {
    try { await api.post(`/service-requests/opportunities/${oppId}/decline`, { reason }); toast.success("Passed on this job"); onDone(); }
    catch (err) { toast.error(err?.response?.data?.error || "Could not decline"); }
  };
  return (
    <div className="rounded-3xl border border-slate-200 bg-white p-5" data-testid="decline-box">
      <div className="text-sm font-semibold text-[#071b22]">Not for you?</div>
      <select value={reason} onChange={(e) => setReason(e.target.value)} className="sr-input mt-2" data-testid="decline-reason">{REASONS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
      <button onClick={submit} className="mt-3 w-full rounded-full border border-slate-300 py-2.5 text-sm font-medium hover:bg-slate-50" data-testid="decline-submit">Pass on this job</button>
    </div>
  );
}

export default function OpportunityDetailPage() {
  const { id } = useParams();
  const [d, setD] = useState(null);
  const load = useCallback(() => api.get(`/service-requests/${id}`).then((r) => setD(r.data)).catch((e) => setD({ error: e?.response?.data?.error || "Not available" })), [id]);
  useEffect(() => { load(); }, [load]);
  useDocumentTitle(d?.request?.title || "Job match");

  if (!d) return <DashboardShell><div className="flex items-center gap-2 text-[#252A34]/60"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div></DashboardShell>;
  if (d.error) return <DashboardShell><div className="rounded-2xl border border-slate-200 bg-white p-8 text-center" data-testid="opp-error">{d.error}</div></DashboardShell>;
  const r = d.request;
  const opp = d.opportunity;
  const canRespond = ["new", "viewed"].includes(opp.status) && ["published", "receiving_proposals"].includes(r.status);

  return (
    <DashboardShell>
      <style>{SR_STYLES}</style>
      <div className="max-w-6xl" data-testid="opportunity-detail">
        <Link to="/dashboard/find-work" className="text-sm text-[#165a71]">← Job matches</Link>
        <div className="mt-3 text-xs font-bold tracking-[0.2em] uppercase text-[#165a71]">{r.questionnaire_name || "Job match"} · {Math.round(opp.match_score)} match score</div>
        <h1 className="mt-1 font-display text-2xl md:text-3xl font-semibold text-[#071b22]" data-testid="opp-title">{r.title}</h1>
        <div className="mt-2 text-xs text-[#252A34]/60 flex flex-wrap gap-3">
          <span>From {r.customer_first_name}</span>
          <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{(r.urgency || "").replace(/_/g, " ")}</span>
          {r.address_snapshot?.city && <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{r.address_snapshot.city}, {r.address_snapshot.state} {r.address_snapshot.zip_code}</span>}
          <span className="inline-flex items-center gap-1"><DollarSign className="h-3.5 w-3.5" />{budgetLabel(r)}</span>
        </div>
        {opp.match_reasons?.length > 0 && <div className="mt-3 flex flex-wrap gap-2">{opp.match_reasons.map((m) => <span key={m} className="rounded-full bg-[#165a71]/8 border border-[#165a71]/20 px-3 py-1 text-xs text-[#165a71]">{m}</span>)}</div>}

        <div className="mt-8 grid gap-6 lg:grid-cols-[1.3fr_1fr] items-start">
          <div className="space-y-6">
            <div className="rounded-3xl border border-slate-200 bg-white p-6" data-testid="opp-brief">
              <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-[#165a71]"><Sparkles className="h-4 w-4" /> Project brief</div>
              <p className="mt-3 text-[#071b22] leading-relaxed">{r.ai_summary}</p>
              {r.description && <p className="mt-3 text-sm text-[#252A34]/75 whitespace-pre-line">{r.description}</p>}
              {(r.require_insurance || r.require_background_check) && <div className="mt-4 flex gap-3 text-xs text-[#1f8a5b]">{r.require_insurance && <span className="inline-flex items-center gap-1"><ShieldCheck className="h-3.5 w-3.5" />Insurance required</span>}{r.require_background_check && <span className="inline-flex items-center gap-1"><ShieldCheck className="h-3.5 w-3.5" />Background check required</span>}</div>}
            </div>
            <div className="rounded-3xl border border-slate-200 bg-white p-6" data-testid="opp-answers">
              <div className="text-xs font-bold uppercase tracking-[0.18em] text-[#165a71] mb-2">Customer answers</div>
              {(r.answers_view || []).map((a) => (
                <div key={a.label} className="flex items-baseline justify-between gap-6 py-2 border-b border-slate-100 last:border-0">
                  <span className="text-xs text-[#252A34]/60">{a.label}</span>
                  <span className="text-sm font-medium text-right">{Array.isArray(a.value) ? a.value.join(", ") : String(a.value)}</span>
                </div>
              ))}
            </div>
            {(r.attachments || []).length > 0 && <div className="grid grid-cols-3 sm:grid-cols-5 gap-3" data-testid="opp-attachments">{r.attachments.map((a) => <AttachmentThumb key={a.id} a={a} />)}</div>}
          </div>
          <div className="space-y-4 lg:sticky lg:top-24">
            {d.my_estimate ? (
              <div className="rounded-3xl border border-emerald-200 bg-emerald-50 p-6" data-testid="opp-my-estimate">
                <div className="text-xs font-bold uppercase tracking-wider text-emerald-800">Your estimate · {d.my_estimate.status}</div>
                <div className="mt-2 font-display text-3xl font-semibold text-[#071b22]">${Number(d.my_estimate.price).toLocaleString()}</div>
                <p className="mt-2 text-sm text-[#252A34]/80">{d.my_estimate.scope}</p>
                {d.my_estimate.recommended_by_admin && <div className="mt-3 rounded-xl bg-white/70 border border-emerald-200 px-3 py-2 text-xs text-emerald-900" data-testid="opp-admin-sent-note">Sent on your behalf by the Gulf Coast Work team. Contact support if anything needs changing.</div>}
              </div>
            ) : canRespond ? (
              <>
                <EstimateForm oppId={opp.id} kind={r.kind} onDone={load} />
                <DeclineBox oppId={opp.id} onDone={load} />
              </>
            ) : (
              <div className="rounded-3xl border border-slate-200 bg-white p-6 text-sm text-[#252A34]/70" data-testid="opp-closed">This opportunity is {opp.status}.</div>
            )}
          </div>
        </div>
      </div>
    </DashboardShell>
  );
}
